// web-v2/src/lib/quoteCompleteness.js
//
// QUOTE-WIZARD-SPEC.md section 5 + section 8. Which of the wizard's questions a
// quote has answered, DERIVED from the fields that are present. Not a column,
// not a boolean a human sets, not something an extractor decides.
//
// Three rules the whole file exists to hold:
//
//   1. ANSWERED AS NONE IS NOT UNANSWERED. An explicit zero deductible is an
//      answer; a blank one is not. isAnswered() therefore accepts 0 and rejects
//      '' / null / undefined, and every caller goes through it.
//
//   2. NOT ASKED IS ITS OWN STATE. A Liability Only quote has no hull value and
//      no deductibles, and those fields are `notAsked`, never `missing`. A
//      question that does not apply is not a gap.
//
//   3. NOTHING HERE BLOCKS ANYTHING. David, 2026-07-29: "save and just flag as
//      incomplete." This function reports; it has no opinion about saving.
//
//   4. IT IS NOT THE PRESENT GATE (client-bind-flow decision 13). It is the
//      wizard's own step progress ("2 questions left", where re-entry lands) and
//      nothing else reads it. Whether a quote may go to a client is the server's
//      list (present_missing), rendered by the helpers at the foot of this file.
//
// Pure and dependency-free so tests/quote-completeness.test.js can import it in
// node. Keep it that way.

// Five steps, not six. The number and the paper were separate until 2026-07-29,
// when building it showed they are one lookup: the premium, the quote number and
// both dates come off the same header block of the same page of the same PDF, and
// the carrier is already on screen as a read-only label. Splitting them cost a
// page turn between two fields an inch apart.
export const WIZARD_STEPS = [
    { key: 'offer', title: 'The offer' },
    // "Coverage" rather than "The terms" (David, 2026-07-30: "'The terms' page
    // (coverage is better, prob?)"). The KEY stays `terms`: it is written into
    // step-scoped save bodies and referenced across three files, and renaming a
    // key to fix a label is how a save path breaks silently.
    { key: 'terms', title: 'Coverage' },
    // TRIA HAD ITS OWN PAGE UNTIL 2026-08-01. David: it "does not warrant its
    // own page". It is one question with three buttons, and a page turn for it
    // cost more than it bought. It now renders at the foot of the coverage step
    // and is REQUIRED there (requiredFor('terms')), so what moved is where it is
    // asked, not whether it is asked.
    { key: 'pilots', title: 'The pilots' },
    { key: 'rest', title: 'The rest' },
];

// What each missing field is called in a sentence a broker reads.
export const FIELD_LABELS = {
    premium: 'premium',
    quote_number: 'quote number',
    proposed_effective: 'effective date',
    coverage_type: 'coverage type',
    aircraft_usage: 'aircraft use',
    liability_per_occurrence: 'liability per occurrence',
    liability_per_passenger: 'liability per passenger',
    hull_value: 'hull value',
    in_motion_deductible: 'in-motion deductible',
    non_motion_deductible: 'not-in-motion deductible',
    tria_status: 'whether TRIA is included, optional or excluded',
    tria_premium: 'the optional TRIA premium',
    pilot_warranty: 'the pilot warranty',
    roster_offerable: 'a roster pilot who cannot be named on the policy',
};

// A value counts as answered when a human put something there. Zero is a value.
// Everything else about this function is the same sentence restated for a type.
export function isAnswered(v) {
    if (v === null || v === undefined) return false;
    if (typeof v === 'string') return v.trim() !== '';
    if (typeof v === 'number') return Number.isFinite(v);
    if (typeof v === 'boolean') return true;
    if (Array.isArray(v)) return v.length > 0;
    return true;
}

// Hull scope from the COVERAGE-TYPE STRING ONLY.
//
// Deliberate frontend mirror of the type-string branches of utils/coverage-type.js
// classifyCoverage (web-v2 does not import repo-root utils; same arrangement as
// QuoteEntrySheet's mirror of utils/policy-term.js). tests/quote-completeness.test.js
// pins the two together so a rename over there fails the gate here.
//
// It mirrors the TYPE branches and NOT the value-disambiguation branch, on
// purpose. That branch reads "a coverage type with no hull and no deductibles is
// liability-only", which is correct for a RENDER of a finished quote and exactly
// wrong here: a half-entered Full Flight quote has no hull and no deductibles
// yet, and calling it liability-only would render its blanks as "not asked".
// Absence is not evidence, so an unknown scope asks for everything.
export function hullScopeFromType(coverageType) {
    const t = String(coverageType || '').toLowerCase().replace(/,/g, '').trim();
    if (!t) return 'unknown';
    if (t.includes('liability only')) return 'liability-only';
    if (t.includes('gnim') || t.includes('ground not in motion') || t === 'g') return 'gnim';
    return 'hull';
}

// Hull is answered when EVERY aircraft on the quote carries one. A fleet quote
// with three tails and two hull values is not a hull value away from done, it is
// a tail away, and reporting the first one it finds would hide that.
function hullAnswered(input) {
    const rows = Array.isArray(input.aircraft) ? input.aircraft.filter(Boolean) : [];
    if (rows.length) return rows.every((r) => isAnswered(r.hull_value));
    return isAnswered(input.hull_value);
}

// The TRIA answer is one question with two fields (QUOTE-WIZARD-SPEC section 3),
// and it is the only place in this file where a present field can still leave the
// question open: included=false with no amount is half an answer.
function triaMissing(input) {
    // FOUR states since 2026-07-30: NULL not answered, included, optional (which
    // must carry a price), excluded. Only 'optional' can leave the question half
    // answered, which is the one case this function exists to catch.
    const status = input.tria_status;
    if (status === null || status === undefined || status === '') return ['tria_status'];
    if (status === 'optional' && !(Number(input.tria_premium) > 0)) return ['tria_premium'];
    return [];
}

// The warranty is stated when the quote says Named Pilots Only, or carries any
// piece of an open pilot warranty. named_pilots_only === false is the column
// default on every row ever written, so it is not evidence of anything.
function warrantyStated(input) {
    if (input.named_pilots_only === true) return true;
    return [
        input.opw_certificate,
        input.opw_ratings,
        input.opw_total_hours,
        input.opw_type_hours,
        input.opw_make_model_hours,
        input.opw_full_text,
    ].some(isAnswered);
}

// Per step: which fields this quote is actually being asked for, and which of
// those are blank. `rest` (underwriter, subjectivities, coverage parts, notes)
// requires nothing. Every one of those is genuinely optional on a P&B quote, so
// a step that flagged them would fire on the ordinary case, which is the thing
// section 8 forbids.
function requiredFor(stepKey, input) {
    const scope = hullScopeFromType(input.coverage_type);
    switch (stepKey) {
        // The offer expiry is NOT required. Plenty of carriers do not state one,
        // and a question the document routinely does not answer would fire on the
        // ordinary case (section 8).
        case 'offer':
            return ['premium', 'quote_number', 'proposed_effective'];
        case 'terms': {
            const out = ['coverage_type', 'aircraft_usage', 'liability_per_occurrence'];
            // A split limit needs its per-passenger sublimit; a smooth (CSL) one
            // does not have a sublimit to be missing.
            if (input.liability_smooth === false) out.push('liability_per_passenger');
            if (scope !== 'liability-only') out.push('hull_value', 'non_motion_deductible');
            // GNIM has no in-motion coverage, so it has no in-motion deductible
            // to answer (utils/coverage-type.js: Not Applicable, never $0).
            if (scope !== 'liability-only' && scope !== 'gnim') out.push('in_motion_deductible');
            // TRIA RIDES THE COVERAGE STEP (2026-08-01). David: it "does not
            // warrant its own page". It is still a REQUIRED answer with its own
            // four states, and fieldAnswered routes it through triaMissing, so
            // folding the page did not soften the question.
            out.push('tria_status');
            return out;
        }
        case 'pilots': {
            const out = ['pilot_warranty'];
            // A roster pilot who cannot be named on the policy is not covered,
            // so a quote that omits one is not finished, however complete its
            // fields look. Asked ONLY when the caller supplied a roster: an
            // absent key must mean "not asked" rather than "unanswered", so a
            // caller with no roster cannot mark every quote on the deal
            // incomplete. That asymmetry is deliberate; the guard in
            // tests/wizard-roster-pilots.test.js pins it.
            if (Array.isArray(input.unofferable_pilots)) out.push('roster_offerable');
            return out;
        }
        default:
            return [];
    }
}

// Which terms-step questions this quote is NOT being asked, so a surface can
// say "no hull on a liability-only quote" instead of leaving a silent hole.
//
// Only a POSITIVE answer makes a field Not Applicable. A quote that has not said
// whether its limit is smooth or split is not "not asked" for the sublimit, it is
// unknown, and it appears in neither list: naming it Not Applicable would be the
// same absence-as-evidence move the rest of this file exists to prevent.
// Declared ABOVE its consumer: a const referenced before its declaration is a
// TDZ crash, which is how /apply went down for 20 hours (29888e6).
function notAskedFor(input) {
    const out = [];
    if (input.liability_smooth === true) out.push('liability_per_passenger');
    const scope = hullScopeFromType(input.coverage_type);
    if (scope === 'liability-only') out.push('hull_value', 'non_motion_deductible', 'in_motion_deductible');
    else if (scope === 'gnim') out.push('in_motion_deductible');
    return out;
}

function fieldAnswered(field, input) {
    if (field === 'hull_value') return hullAnswered(input);
    if (field === 'pilot_warranty') return warrantyStated(input);
    if (field === 'tria_status') return triaMissing(input).length === 0;
    // Answered means every roster pilot CAN be offered. An empty array is the
    // answer "everyone can be named", which is why this reads the length, not
    // isAnswered (which rejects an empty array).
    if (field === 'roster_offerable') return (input.unofferable_pilots || []).length === 0;
    return isAnswered(input[field]);
}

/**
 * @param {object} quote flat quote shape (the GET /quotes/:id detail shape,
 *   through quoteWizardModel completenessInput). Keys read:
 *   premium, quote_number, proposed_effective, coverage_type, aircraft_usage,
 *   liability_per_occurrence, liability_per_passenger, liability_smooth,
 *   hull_value, aircraft[{hull_value}], in_motion_deductible,
 *   non_motion_deductible, tria_status, tria_premium, named_pilots_only,
 *   opw_* .
 * @returns {{steps: Array, missingCount: number, firstIncompleteIndex: number, complete: boolean}}
 */
export function computeQuoteCompleteness(quote) {
    const input = quote || {};
    const steps = WIZARD_STEPS.map((s) => {
        const required = requiredFor(s.key, input);
        const answered = [];
        const missing = [];
        for (const f of required) {
            (fieldAnswered(f, input) ? answered : missing).push(f);
        }
        // TRIA reports WHICH half is open, so "optional with no amount" reads as
        // a missing amount rather than a missing toggle. It rides the coverage
        // step now (2026-08-01) rather than a page of its own, so the expansion
        // happens in place of the tria_status entry instead of over the whole
        // list: a coverage step missing a deductible AND a TRIA answer has to
        // say both.
        const triaAt = missing.indexOf('tria_status');
        if (triaAt !== -1) missing.splice(triaAt, 1, ...triaMissing(input));
        const notAsked = s.key === 'terms' ? notAskedFor(input) : [];
        return {
            key: s.key,
            title: s.title,
            required,
            answered,
            missing,
            notAsked,
            complete: missing.length === 0,
        };
    });
    const missingCount = steps.reduce((n, s) => n + s.missing.length, 0);
    const firstIncompleteIndex = steps.findIndex((s) => !s.complete);
    return { steps, missingCount, firstIncompleteIndex, complete: missingCount === 0 };
}

// One line for a surface that has room for one line.
export function completenessSummary(result) {
    if (!result || result.complete) return '';
    const fields = result.steps.flatMap((s) => s.missing).map((f) => FIELD_LABELS[f] || f);
    if (fields.length <= 3) return `No ${fields.join(', no ')}`;
    return `No ${fields.slice(0, 2).join(', no ')}, and ${fields.length - 2} more`;
}

// ── THE SERVER'S PRESENT LIST, ROUTED AND WORDED (decision 13) ─────────────
//
// Whether a quote may be shown is decided ONCE, on the server
// (utils/quote-present-readiness.js presentMissing, served as present_missing
// on GET /present-options and on every save answer, refused as 422
// QUOTE_NOT_PRESENTABLE). Nothing below decides it. fromPresentRow, which
// scored a present-sheet row in the browser through a second derivation (and
// a liability_per_occurrence ?? liability_csl coalesce), is deleted.
//
// FIELD_TO_STEP is ROUTING, not a rule: which wizard step holds the field a
// server item names, so the item opens its field. An item whose field no
// screen here can edit says why instead of offering a button.
export const FIELD_TO_STEP = Object.freeze({
    coverage_type: 'terms', liability_limit: 'terms', hull_value: 'terms',
    deductible_in_motion: 'terms', deductible_not_in_motion: 'terms', tria_premium: 'terms', options: 'terms',
    term: 'offer', premium: 'offer', effective_date: 'offer',
    pilot_clause: 'pilots', pilot_list: 'pilots',
});
export const NO_DOOR = Object.freeze({
    // The wizard never sends a carrier and an edit never changes it (70c02bd):
    // a different carrier is a different quote.
    carrier: 'A different carrier is a different quote: log it again under a carrier on your list.',
    property_limit: 'Log it as a hangar or commercial quote.',
});
// The two envelope shapes that have an edit sheet here.
export const ENVELOPE_SHEETS = Object.freeze({ hangar: 'hangar', commercial_gl: 'gl' });

/**
 * Where one server item opens. A pilot off the deal's pilot list is fixed on
 * the deal roster, whatever the quote's shape, so it opens the roster sheet; an
 * unread list has nothing to open. Aircraft quotes open the wizard on the item's
 * step; an envelope quote opens its own log sheet whatever the item.
 * @param {object} item a present_missing entry ({ key, label, field, ... })
 * @param {{policyType?: string|null}} quote the quote's policy_type (null = aircraft)
 * @returns {{kind: 'roster'}|{kind: 'wizard', step: string, field: string}|{kind: 'envelope', sheet: string}|{kind: 'none', why: string|null}}
 */
export const PILOT_LIST_UNREADABLE_WHY = "Could not read the deal's pilot list. Close and reopen to try again.";
export function missingItemDoor(item, { policyType = null } = {}) {
    if (!item || typeof item !== 'object') return { kind: 'none', why: null };
    if (item.key === 'pilot_not_on_deal_list') return { kind: 'roster' };
    if (item.key === 'pilot_list_unreadable') return { kind: 'none', why: PILOT_LIST_UNREADABLE_WHY };
    if (policyType) {
        const sheet = ENVELOPE_SHEETS[policyType];
        return sheet ? { kind: 'envelope', sheet } : { kind: 'none', why: 'No sheet here edits this kind of quote.' };
    }
    const step = FIELD_TO_STEP[item.field];
    if (step) return { kind: 'wizard', step, field: item.field };
    return { kind: 'none', why: NO_DOOR[item.field] || null };
}

// The server's own words for its items, in its order.
export function missingText(items) {
    return (Array.isArray(items) ? items : []).map((i) => (i && i.label) || (i && i.key) || '').filter(Boolean).join(', ');
}

/**
 * What a SAVE answer says about showing the quote (S5: a save always lands;
 * only showing can be refused). `refused`: the save asked to show and the gate said
 * no, so the client sees nothing. `stillShown`: the quote was already visible
 * and no longer passes. A total blanked or zeroed after presentation hides it
 * from the client until it is stated (ruling A, utils/client-quote-visible.js);
 * anything else missing leaves it shown and selectable, and reaches David as
 * FYI on the bind request if the client binds it.
 * @returns {{refused: string|null, stillShown: string|null}}
 */
export function presentSaveNotices(answer) {
    const a = answer || {};
    const r = a.present_refused;
    if (r) {
        const why = r.message || (missingText(r.missing) ? `Missing: ${missingText(r.missing)}.` : 'The present check refused it.');
        return { refused: `Saved, not shown to the client. ${why}`, stillShown: null };
    }
    if (a.client_visible === true && Array.isArray(a.present_missing) && a.present_missing.length) {
        const missing = `Missing: ${missingText(a.present_missing)}.`;
        return {
            refused: null,
            stillShown: a.present_missing.some((i) => i && i.key === 'premium')
                ? `Hidden from the client until its total premium is stated. ${missing}`
                : `Still shown to the client and still selectable. ${missing} If the client binds it, the bind request lists these as FYI.`,
        };
    }
    return { refused: null, stillShown: null };
}

// ── WHAT QUICK-LOG SEEDED FROM THE APPLICATION (B6) ──────────────────────
//
// The server lists it (routes/tracon.routes.js asAppliedFields, on the compose
// read and on the log's answer); this only words it, beside the values the
// same answer carries, so David can see what he did not type.
export const FROM_APPLICATION_LABELS = Object.freeze({
    coverage_type: 'coverage type', liability_per_occurrence: 'liability per occurrence',
    liability_per_passenger: 'liability per passenger', medical_per_person: 'medical per person',
    in_motion_deductible: 'in-motion deductible', non_motion_deductible: 'not-in-motion deductible',
    proposed_effective: 'effective date', make_model: 'make and model', year: 'year', hull_value: 'hull value',
    aircraft_use: 'aircraft use', deductible_in_motion: 'in-motion deductible', deductible_not_in_motion: 'not-in-motion deductible',
});

const MONEY_KEYS = new Set(['liability_per_occurrence', 'liability_per_passenger', 'medical_per_person', 'in_motion_deductible',
    'non_motion_deductible', 'hull_value', 'deductible_in_motion', 'deductible_not_in_motion']);

/**
 * @param {{header?: string[], aircraft?: Array<{tail_number_id, fields: string[]}>}} fromApp
 * @param {object} [body] the as-applied body the same answer carries (values)
 * @param {Array} [aircraft] the deal's tails, for display names
 * @returns {string[]} one entry per seeded field, e.g. 'hull value: $85,000 (N123AB)'
 */
export function fromApplicationLines(fromApp, body = null, aircraft = []) {
    const f = fromApp || {};
    const val = (k, v) => {
        if (v == null || v === '') return '';
        const n = Number(v);
        return MONEY_KEYS.has(k) && String(v).trim() !== '' && Number.isFinite(n) ? `: $${n.toLocaleString('en-US')}` : `: ${v}`;
    };
    const out = (f.header || []).map((k) => `${FROM_APPLICATION_LABELS[k] || k}${val(k, body && body[k])}`);
    const sched = (body && Array.isArray(body.aircraft_schedule)) ? body.aircraft_schedule : [];
    for (const a of f.aircraft || []) {
        const row = sched.find((r) => String(r.tail_number_id) === String(a.tail_number_id)) || {};
        const tail = (aircraft || []).find((t) => String(t.tail_number_id) === String(a.tail_number_id));
        const name = (tail && tail.tail_display) || a.tail_number_id;
        for (const k of a.fields || []) out.push(`${FROM_APPLICATION_LABELS[k] || k}${val(k, row[k])} (${name})`);
    }
    return out;
}
